import { Device } from '@/store/device/types';
import { calmLogger } from '@/utils/calmLogger';
import { openAppDeeplinkOrAppStore } from '@/utils/deepLink';
import { CALM_ONE_LINKS } from '@/utils/deeplinks/deeplinks';
import { deviceCouldDownloadApp } from '@/utils/device';
import getUrl from '@/utils/getUrl';

import { PageConfigSettings } from './types';

// WHATWG URL lowercases the scheme, so a hand-typed 'CALM://' compares equal here too.
const CALM_APP_SCHEME = 'calm:';
const ONELINK_HOST = 'calm.onelink.me';
const CALM_APEX_HOST = 'calm.com';
const CALM_HOST_SUFFIX = '.calm.com';

/** The destination of the success screen's primary CTA, resolved once from page_config. */
export interface PostRedemptionCta {
	/** The normalised href of the first entry that passed the allowlist, or the Calm OneLink default. */
	href: string;
	/** A `calm://` app deeplink, which a browser cannot follow directly. See `openPostRedemptionCta`. */
	isAppDeeplink: boolean;
	/** False when no admin-configured link survived the allowlist and the Calm OneLink default is used. */
	isConfigured: boolean;
}

/**
 * Parses `raw` and returns it only if it is a destination Calm controls: the `calm://` app scheme,
 * Calm's OneLink domain (which carries its own install-or-open and web fallbacks), or https on
 * calm.com. `post_redemption_deeplinks` is admin-authored text with no validation on the write
 * side that ends up as a navigation target on a public page, so everything else is rejected,
 * `javascript:` and `http:` included. Returns the parsed URL so callers navigate to its normalised
 * `href`, never to the raw string.
 */
export function parseAllowedPostRedemptionUrl(raw: string): URL | null {
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		return null;
	}
	if (url.protocol === CALM_APP_SCHEME) return url;
	if (url.protocol !== 'https:') return null;
	const host = url.hostname.toLowerCase();
	const isAllowedHost = host === ONELINK_HOST || host === CALM_APEX_HOST || host.endsWith(CALM_HOST_SUFFIX);
	return isAllowedHost ? url : null;
}

/**
 * The first allowed entry wins and the rest are ignored. The schema is a bare list of URLs with
 * no labels, so the user could not tell a second button from the first anyway. Growing the schema
 * to labelled links is a separate api + admin change.
 */
export function resolvePostRedemptionCta(settings: PageConfigSettings | undefined): PostRedemptionCta {
	const raw = settings?.post_redemption_deeplinks;
	const candidates = Array.isArray(raw) ? raw : [];
	for (const entry of candidates) {
		const url = typeof entry === 'string' ? parseAllowedPostRedemptionUrl(entry.trim()) : null;
		if (url) {
			return { href: url.href, isAppDeeplink: url.protocol === CALM_APP_SCHEME, isConfigured: true };
		}
	}
	return { href: CALM_ONE_LINKS.app, isAppDeeplink: false, isConfigured: false };
}

/**
 * Web paths for the app routes whose web route is not `/app/<route>`, taken from the OneLink
 * pairs Calm already ships in utils/deeplinks/deeplinks.ts (each `af_dp` next to its `af_web_dp`).
 * Keyed by the lowercased first path segment of the `calm:` deeplink. A contract test in
 * postRedemptionCta.test.ts replays every OneLink pair through `appDeeplinkToWebUrl`, so a change
 * to that table without a matching change here fails there instead of drifting silently.
 */
const WEB_PATH_BY_APP_ROUTE: Record<string, string> = {
	'': '/app',
	home: '/app',
	'daily-calm': '/app/meditate',
	login: '/login',
};

/** App routes known to mirror onto `/app/<route>/...` on the web, from the same OneLink table. */
const MIRRORED_APP_ROUTES = new Set(['program']);

/**
 * Desktop browsers cannot open `calm://`, so the app path is mapped onto the web app instead.
 * Routes with a known web counterpart use it; anything else is approximated as `/app/<path>` and
 * logged, because the web app does not mirror every app route and nothing on the admin write side
 * validates the deeplink. Admins should prefer OneLink URLs, which carry their own web destination.
 */
export function appDeeplinkToWebUrl(deeplink: string): string {
	const url = new URL(deeplink);
	// 'calm://program/x' parses as host 'program' + pathname '/x'; 'calm:/home' as host '' + pathname '/home'.
	const [rawRoute = '', ...rest] = `${url.host}${url.pathname}`.replace(/^\/+|\/+$/g, '').split('/');
	// The first segment is the app route name, which the web app spells in lowercase. WHATWG parsing
	// keeps a non-special scheme's host as typed (unlike https), so 'calm://Program/x' would otherwise
	// land on '/app/Program/x'. The remaining segments stay as typed because they can be
	// case-sensitive ids.
	const route = rawRoute.toLowerCase();
	const restPath = rest.length > 0 ? `/${rest.join('/')}` : '';
	const www = getUrl('www');

	// Anything after the route is kept, on a known route too. Dropping it would send the user
	// somewhere the admin did not point at, silently.
	const knownPath = WEB_PATH_BY_APP_ROUTE[route];
	const webUrl =
		knownPath !== undefined
			? `${www}${knownPath}${restPath}${url.search}`
			: `${www}/app/${route}${restPath}${url.search}`;

	// Verified means the OneLink table vouches for exactly this shape: a known route with nothing
	// after it, or a mirrored route with an id after it. A bare mirrored route (`calm://program`)
	// has no page on either side, so it is approximated and logged like any unknown route.
	const isVerified =
		knownPath !== undefined ? rest.length === 0 : MIRRORED_APP_ROUTES.has(route) && rest.length > 0;
	if (!isVerified) {
		// The only signal that a campaign's desktop CTA lands on a route the web app may not have.
		calmLogger.warn('Campaign deeplink has no verified web equivalent, approximating', {
			deeplink,
			webUrl,
		});
	}
	return webUrl;
}

/**
 * Navigates with `window.location`, never the Next router. `useRouterPush` merges the current
 * query string into whatever it pushes, which would carry `offer_code` and the UTM params onto an
 * external URL, and the router cannot follow a custom scheme at all.
 *
 * The destination is validated again here, at the sink, and the navigation uses the parsed URL's
 * own href. A `PostRedemptionCta` built anywhere other than `resolvePostRedemptionCta` therefore
 * still cannot send the user outside the allowlist. It falls back to the Calm OneLink instead.
 */
export async function openPostRedemptionCta(
	cta: PostRedemptionCta,
	device: Device | undefined,
): Promise<void> {
	const url = parseAllowedPostRedemptionUrl(cta.href) ?? new URL(CALM_ONE_LINKS.app);

	if (url.protocol !== CALM_APP_SCHEME) {
		window.location.assign(url.href);
		return;
	}

	if (device && deviceCouldDownloadApp(device)) {
		// Settles once the store fallback has fired, so a caller holding a press guard keeps it
		// held for the whole window in which a second press would queue a second fallback.
		await openAppDeeplinkOrAppStore(url.href, device);
		return;
	}

	window.location.assign(appDeeplinkToWebUrl(url.href));
}
